/**
 * RecordDetailDock — dokowany widok dzielony (split view) dla kartotek. Renderuje
 * formularz kartoteki (children) po lewej stronie i zawartość panelu bocznego
 * (panelContent) po prawej, oddzielone przeciąganym suwakiem (drag resize).
 * Szerokość panelu jest zapamiętywana per moduł w localStorage, panel można
 * zwinąć/rozwinąć. Komponent jest czysto layoutowy/prezentacyjny — nie zna
 * treści panelu (docelowo osadzi tam podglądarkę załączników), otrzymuje ją
 * przez prop `panelContent`.
 */

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelRightOpen } from 'lucide-react';

export interface RecordDetailDockPanelRenderProps {
  /** Zwija cały dok do pionowego paska (chowa panel podglądu). Stan `collapsed` żyje w doku. */
  onCollapse: () => void;
}

export interface RecordDetailDockProps {
  /** Identyfikator modułu — używany do budowy klucza localStorage (per-moduł szerokość). */
  moduleId: string;
  /**
   * Zawartość panelu bocznego (podglądarka załączników). Może być gotowym węzłem albo
   * funkcją render-prop otrzymującą `{ onCollapse }` — dzięki temu przycisk „ukryj"
   * wewnątrz panelu zwija cały dok (inaczej stan `collapsed` byłby nieosiągalny).
   */
  panelContent: ReactNode | ((props: RecordDetailDockPanelRenderProps) => ReactNode);
  /** Formularz kartoteki renderowany po lewej stronie widoku dzielonego. */
  children: ReactNode;
  /** Domyślna szerokość panelu (px) używana gdy brak zapisanej wartości w localStorage. Domyślnie 480. */
  defaultWidth?: number;
  /** Czy panel wystartuje zwinięty. Domyślnie false. */
  defaultCollapsed?: boolean;
}

const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.7;
const DEFAULT_WIDTH = 480;

function clampWidth(width: number): number {
  const maxWidth =
    typeof window !== 'undefined' ? Math.round(window.innerWidth * MAX_WIDTH_RATIO) : 900;
  return Math.min(maxWidth, Math.max(MIN_WIDTH, width));
}

export function RecordDetailDock({
  moduleId,
  panelContent,
  children,
  defaultWidth = DEFAULT_WIDTH,
  defaultCollapsed = false,
}: RecordDetailDockProps) {
  const { t } = useTranslation();
  const storageKey = `doc-dock-width-${moduleId}`;

  const [width, setWidth] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return clampWidth(Number.isFinite(parsed) ? parsed : defaultWidth);
  });
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);
  const draggingRef = useRef(false);
  // Świeża szerokość dla zapisu w onUp. Jedyny zapisujący to onMove (synchronicznie) —
  // NIE dodawaj efektu `widthRef.current = width`: byłby redundantny i mylnie wyglądałby
  // na mechanizm naprawiający wyścig (naprawia go synchroniczny zapis w onMove).
  const widthRef = useRef(width);
  const containerRef = useRef<HTMLDivElement>(null);

  const onSplitterMouseDown = useCallback(() => {
    draggingRef.current = true;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      const right = containerRef.current.getBoundingClientRect().right;
      const next = clampWidth(right - e.clientX);
      // Zapis do ref synchronicznie tu, NIE w efekcie po renderze: szybki (lub
      // skryptowany) drag potrafi wpuścić mousemove i mouseup w jedną paczkę, a wtedy
      // onUp odczytałby ref sprzed commitu i zapisał starą szerokość.
      widthRef.current = next;
      setWidth(next);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.localStorage.setItem(storageKey, String(widthRef.current));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [storageKey]);

  if (collapsed) {
    return (
      <div className="flex items-stretch gap-0">
        <div className="flex-1 min-w-0">{children}</div>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="flex-shrink-0 w-10 self-stretch flex flex-col items-center justify-center gap-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg ml-3 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors group"
          title={t('attachmentPreview.expandPanel')}
        >
          <PanelRightOpen className="w-4 h-4 text-gray-400 group-hover:text-accent-600" />
          <span className="text-[11px] font-medium text-gray-400 group-hover:text-accent-600 [writing-mode:vertical-rl] rotate-180 tracking-wide">
            {t('attachmentPreview.panelLabel')}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex items-stretch gap-0">
      <div className="flex-1 min-w-0">{children}</div>

      {/* Suwak resize */}
      <div
        onMouseDown={onSplitterMouseDown}
        className="flex-shrink-0 w-3 cursor-col-resize flex items-center justify-center group select-none"
        title={t('attachmentPreview.dragToResize')}
      >
        <div className="w-1 h-14 rounded-full bg-gray-200 dark:bg-gray-700 group-hover:bg-accent-400 transition-colors" />
      </div>

      {/* Dokowany panel boczny */}
      <aside
        className="flex-shrink-0 sticky top-4 self-start"
        style={{ width: `${width}px`, height: 'calc(100vh - 8rem)' }}
      >
        {typeof panelContent === 'function'
          ? panelContent({ onCollapse: () => setCollapsed(true) })
          : panelContent}
      </aside>
    </div>
  );
}

export default RecordDetailDock;
